var DSModal = DSModal || {};

defineDs('DanskeSpil/Domain/Feature.Components/Scripts/ConsentModal.js', [
  'DanskeSpil/Framework/PlayerAccountManagement/Scripts/Helpers/Consent',
  'DanskeSpil/Domain/Authentification/Scripts/LoginController',
  'DanskeSpil/Domain/Authentification/Scripts/LoginCache',
  'Shared/Framework/Mithril/Scripts/Helpers/Storage',
], function (ConsentApi, LoginController, LoginCache, Storage) {

  if (!document.querySelector('.js-modal')) {
    return;
  }

  const STORAGE_KEY = 'DESIGNSYSTEM_PERMISSION';

  const focusedCheckboxClasses = ['border-checkbox-focused', 'bg-checkbox-focused'];
  const enabledButtonClasses = ['bg-button-primary', 'text-button-primary', 'border-button-primary', 'hover:bg-button-primary-hover', 'hover:border-button-primary-hover', 'focus:button-primary-focus', 'cursor-pointer'];
  const disabledButtonClasses = ['bg-button-primary-disabled', 'text-button-primary-disabled', 'border-button-primary-disabled', 'hover:bg-button-primary-disabled', 'cursor-not-allowed'];

  const fetchConsentData = async () => {
    try {
      const response = await ConsentApi.getConsent();
      return response?.[0] || null;
    } catch (error) {
      console.error('Error fetching consent data:', error);
      return null;
    }
  };

  const updateConsentSettings = async (consentData, permissionReference, isAccepted) => {
    try {
      const consentItems = [{ id: consentData.mainConsents?.[0].id, accepted: isAccepted }];
      consentData.subConsents.forEach((subConsent) => {
        consentItems.push({ id: subConsent.id, accepted: isAccepted });
      });

      await ConsentApi.setConsent({
        consentItems: consentItems,
        reference: permissionReference
      });

      return true;
    } catch (error) {
      console.error('Error updating consent:', error);
      return false;
    }
  };

  const getCurrentPlayerId = () => LoginCache.getPlayerId() ?? 'anonymous';

  const getStoredPermission = (storageKey) => {
    return Storage.get(`${storageKey}_${getCurrentPlayerId()}`, { skipContext: false });
  };

  const savePermissionToStorage = (modal, permissionReference, storageKey) => {
    const cookieExpirationDays = parseInt(modal.dataset.cookieExpiration, 10) || 0;
    const maxAgeInSeconds = cookieExpirationDays * 24 * 60 * 60;
    Storage.set(`${storageKey}_${getCurrentPlayerId()}`, permissionReference, maxAgeInSeconds, { skipContext: false });
  };

  const updatePermissionUI = (consentCheckbox, modal) => {
    const checkboxWrapper = consentCheckbox.parentElement;
    const acceptButton = modal.querySelector('button.js-permission-accept');
    if (consentCheckbox.checked) {
      checkboxWrapper.querySelector('.js-permission')?.classList.add(...focusedCheckboxClasses);
      acceptButton?.classList.remove(...disabledButtonClasses);
      acceptButton?.classList.add(...enabledButtonClasses);
    } else {
      checkboxWrapper.querySelector('.js-permission')?.classList.remove(...focusedCheckboxClasses);
      acceptButton?.classList.remove(...enabledButtonClasses);
      acceptButton?.classList.add(...disabledButtonClasses);
    }
  };

  const configureConsentCheckbox = (consentCheckbox, consentData, isLoggedIn, updateUICallback) => {
    if (!consentCheckbox || !isLoggedIn) return;

    consentCheckbox.parentElement.classList.remove('hidden');
    consentCheckbox.checked = consentData?.mainConsents?.[0].state === 'Accepted';
    updateUICallback();
  };

  const handleModalClose = (modalId, wasSuccessful, event, modal) => {
    if (wasSuccessful) DSModal.onHide(modalId, event);
    else modal.querySelector('.text-input-select-error-text')?.classList.remove('hidden');
  };

  const setupAlternativeCloseHandlers = (modal, modalId) => {
    const closeIcon = modal.querySelector('.js-permission-close > svg');
    if (closeIcon) {
      modal.querySelector(':scope > div:first-child').addEventListener('click', (event) => {
        handleModalClose(modalId, true, event, modal);
      });
    }
    window.addEventListener('keydown', (event) => {
      const isModalOpen = modal.classList.contains('flex');
      if (closeIcon && isModalOpen && event.key === 'Escape') {
        handleModalClose(modalId, true, event, modal);
      }
    });
  };

  const setupButtonClickHandlers = (modal, modalId, actionCallback, consentCheckbox, configureCallback, saveStorageCallback) => {
    modal.querySelectorAll('.js-permission-close, .js-modal-actions button, .js-modal-actions  a').forEach((button) => {
      button.addEventListener('click', (event) => {
        const isButtonDisabled = button.classList.contains('cursor-not-allowed');
        if (isButtonDisabled) return;

        if (actionCallback && (button.classList.contains('js-permission-accept') || button.classList.contains('js-permission-decline'))) {
          actionCallback(button.classList.contains('js-permission-accept') ? consentCheckbox : null).then((result) => {
            handleModalClose(modalId, result, event, modal);
          });
        } else {
          configureCallback();
          handleModalClose(modalId, true, event, modal);
        }

        saveStorageCallback();
      });
    });
  };

  const updateModalContent = (modal, consentData, consentCheckbox) => {
    modal.querySelector('h2').innerText = consentData.title;
    modal.querySelector('h3').innerText = consentData.subTitle;
    modal.querySelector('h3 ~ p').classList.add('hidden');
    consentCheckbox.nextElementSibling.innerText = consentData.text;
  };

  const initializeModal = (modalId, isLoggedIn) => {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    let consentData;
    const permissionReference = modal.dataset.itemName.toUpperCase();
    const shouldOpenByDefault = () => modal.dataset.defaultOpen === 'true';
    const consentCheckbox = modal.querySelector('.js-permission + input');

    const updateUICallback = () => updatePermissionUI(consentCheckbox, modal);
    const configureCallback = () => configureConsentCheckbox(consentCheckbox, consentData, isLoggedIn, updateUICallback);
    const saveStorageCallback = () => savePermissionToStorage(modal, permissionReference, STORAGE_KEY);

    let consentActionHandler = null;

    if (consentCheckbox && !isLoggedIn) {
      setupAlternativeCloseHandlers(modal, modalId);
      setupButtonClickHandlers(modal, modalId, consentActionHandler, consentCheckbox, configureCallback, saveStorageCallback);
      return;
    }

    if (consentCheckbox && isLoggedIn) {
      consentCheckbox.addEventListener('change', updateUICallback);

      fetchConsentData()
        .then((response) => {
          if (response) {
            consentData = response;
            updateModalContent(modal, consentData, consentCheckbox);

            consentActionHandler = async (checkboxElement) => {
              const isAccepted = checkboxElement ? checkboxElement.checked : false;
              return await updateConsentSettings(consentData, permissionReference, isAccepted);
            };
          }
        })
        .then(() => {
          configureCallback();
          if (shouldOpenByDefault() && getStoredPermission(STORAGE_KEY) !== permissionReference && consentData?.mainConsents?.[0].state !== 'Accepted') {
            DSModal.onShow(modalId);
          }
          setupButtonClickHandlers(modal, modalId, consentActionHandler, consentCheckbox, configureCallback, saveStorageCallback);
        })
        .catch(() => {
          setupButtonClickHandlers(modal, modalId, consentActionHandler, consentCheckbox, configureCallback, saveStorageCallback);
        });
    } else {
      if (shouldOpenByDefault() && !consentCheckbox && getStoredPermission(STORAGE_KEY) !== permissionReference) {
        DSModal.onShow(modalId);
      }
      setupButtonClickHandlers(modal, modalId, consentActionHandler, consentCheckbox, configureCallback, saveStorageCallback);
    }

    setupAlternativeCloseHandlers(modal, modalId);
  };

  DSModal.onShow = (modalId) => {
    const dialog = document.getElementById(modalId);
    dialog?.classList.add('flex');
    dialog?.classList.remove('hidden');
  };

  DSModal.onHide = (modalId, e) => {
    e?.stopPropagation();
    const dialog = document.getElementById(modalId);
    dialog?.classList.add('hidden');
    dialog?.classList.remove('flex');
    dialog?.querySelector('.text-input-select-error-text')?.classList.add('hidden');
  };

  const setupModal = (modalElement) => {
    if (modalElement.querySelector('.js-permission + input')) {
      LoginController.getLoginStatus((isLoggedIn) => {
        initializeModal(modalElement.id, isLoggedIn);
      });
    } else {
      initializeModal(modalElement.id);
    }
  };

  document.querySelectorAll('.js-modal')?.forEach((modalElement) => {
    setupModal(modalElement);
  });
});
